/**
 * Fase B / B5 — Ordens (DEMO-03, dados.ts e precos.ts): Ordem de Compra ao
 * Fornecedor (OCF, só Venda), lançamentos de faturamento como EVENTO, estado
 * CALCULADO, cancelamento só do saldo, substituta, alocação do frete e a
 * situação do Pedido pelas Ordens válidas. Mesmas regras no servidor e na tela.
 */
import { arred, normalizarFrete, type Frete } from './propostas.ts';
import type { ConteudoPedido } from './pedidos.ts';

export type EstadoOrdem = 'backlog' | 'faturada_parcialmente' | 'faturada' | 'faturada_saldo_cancelado' | 'cancelada';
export const ESTADOS_ORDEM: Record<EstadoOrdem, string> = { backlog: 'Backlog', faturada_parcialmente: 'Faturada parcialmente', faturada: 'Faturada', faturada_saldo_cancelado: 'Faturada parcialmente · saldo cancelado', cancelada: 'Cancelada' };

export interface ItemOrdem {
  itemId: string;
  produtoId: string;
  quantidade: number;
  custoUnit: number;
  precoUnit: number;
  disponibilidade: string;
}
export interface LancamentoItens {
  itens: { itemId: string; quantidade: number }[];
  estornado?: boolean;
}

/** Quantidade faturada ATIVA (lançamentos não estornados) de um item. */
export const faturadoAtivo = (lancamentos: LancamentoItens[], itemId: string) => lancamentos.filter((f) => !f.estornado).reduce((s, f) => s + f.itens.filter((i) => i.itemId === itemId).reduce((t, i) => t + i.quantidade, 0), 0);

/** Estado calculado: cancelamento sem faturamento = Cancelada; com faturamento = "Faturada parcialmente · saldo cancelado" (terminal). */
export function estadoOrdem(itens: Pick<ItemOrdem, 'itemId' | 'quantidade'>[], lancamentos: LancamentoItens[], cancelada: boolean): EstadoOrdem {
  const total = itens.reduce((s, i) => s + i.quantidade, 0);
  const faturada = itens.reduce((s, i) => s + faturadoAtivo(lancamentos, i.itemId), 0);
  if (cancelada) return faturada > 0 ? 'faturada_saldo_cancelado' : 'cancelada';
  return faturada === 0 ? 'backlog' : faturada >= total ? 'faturada' : 'faturada_parcialmente';
}

/** Saldo (unidades) ainda a faturar; Ordem cancelada não tem saldo. */
export const saldoOrdem = (itens: Pick<ItemOrdem, 'itemId' | 'quantidade'>[], lancamentos: LancamentoItens[], cancelada: boolean) => (cancelada ? 0 : itens.reduce((s, i) => s + i.quantidade - faturadoAtivo(lancamentos, i.itemId), 0));

export type Alocacao = Record<string, number>; // entidadeId → valor do frete da Ordem

/** Sugestão inicial (editável): integral se há uma só Ordem; integral na entidade da cotação do frete; senão zero em todas. */
export function sugerirAlocacao(entidades: string[], frete: Frete, total: number): Alocacao {
  const f = normalizarFrete(frete);
  const zero = Object.fromEntries(entidades.map((e) => [e, 0]));
  if (f.modalidade === 'CIF' || total <= 0 || entidades.length === 0) return zero;
  if (entidades.length === 1) return { [entidades[0]]: arred(total) };
  if (f.entidadeId && entidades.includes(f.entidadeId)) return { ...zero, [f.entidadeId]: arred(total) };
  return zero;
}

/** Confere a alocação: total do frete, total alocado e diferença (precisa ser zero, sem valores negativos). */
export function conferirAlocacao(total: number, alocacao: Alocacao) {
  const alocado = arred(Object.values(alocacao).reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0));
  const negativo = Object.values(alocacao).some((v) => !Number.isFinite(v) || v < 0);
  const diferenca = arred(total - alocado);
  return { total: arred(total), alocado, diferenca, ok: diferenca === 0 && !negativo };
}

/** Ordem vista pelas regras do Pedido: itens, frete alocado, cancelamento e o faturado ativo por item. */
export interface OrdemDoPedido {
  itens: Pick<ItemOrdem, 'itemId' | 'quantidade'>[];
  freteValor: number;
  cancelada: boolean;
  faturadoPorItem: Record<string, number>;
  /** P3-35: false quando o saldo saiu por substituta ou por "Retificação" (não reduz a venda; sem dupla subtração) */
  cancelamentoEfetivo?: boolean;
}

/** Frete do Pedido ainda não alocado a Ordens (as canceladas liberam a sua parcela). */
export function freteAAlocar(p: Pick<ConteudoPedido, 'frete'>, ordens: OrdemDoPedido[]) {
  if (normalizarFrete(p.frete).modalidade === 'CIF') return 0;
  const jaAlocado = ordens.filter((o) => !o.cancelada).reduce((s, o) => s + o.freteValor, 0);
  return Math.max(0, arred(p.frete.valor - jaAlocado));
}

/** Quantidade de um item do Pedido já coberta por Ordens: não canceladas (a quantidade) + canceladas (só o já faturado). */
export const quantidadeEmOrdens = (ordens: OrdemDoPedido[], itemId: string) => ordens.reduce((s, o) => s + o.itens.filter((i) => i.itemId === itemId).reduce((t, i) => t + (o.cancelada ? (o.faturadoPorItem[i.itemId] ?? 0) : i.quantidade), 0), 0);

export type SituacaoPedido = 'Em andamento' | 'Parcialmente cancelado' | 'Faturado parcialmente' | 'Faturado parcialmente · saldo cancelado' | 'Faturado' | 'Cancelado';

/**
 * Situação do Pedido tipo Venda, calculada pelas Ordens (tabela C12 aprovada em 09/10/2026). Quantidade válida = itens
 * com entidade faturadora; faturado = lançamentos ativos de todas as Ordens (inclusive o já faturado de uma Ordem depois
 * cancelada; o saldo cancelado nunca conta como faturado). "Ordem aberta" = não cancelada e com saldo a faturar
 * (uma substituta aberta mantém o Pedido ativo).
 *  - sem Ordens, ou Ordens abertas sem faturamento → Em andamento;
 *  - todas as Ordens canceladas, nada faturado, sem substituta → Cancelado (C12.3 A);
 *  - faturamento parcial com Ordem aberta → Faturado parcialmente;
 *  - faturamento parcial, saldo cancelado e nenhuma Ordem aberta → Faturado parcialmente · saldo cancelado (C12.6 B, terminal);
 *  - quantidades válidas todas faturadas → Faturado.
 * P3 (16/10/2026, PED-000037): quantidade válida que nunca entrou em Ordem é item ativo — o Pedido não fica "Cancelado"
 * nem no terminal "saldo cancelado" enquanto ela existir (nada é cancelado automaticamente):
 *  - todas as Ordens canceladas, nada faturado, item ainda sem Ordem → Parcialmente cancelado;
 *  - faturamento parcial, saldo cancelado, item ainda sem Ordem → Faturado parcialmente.
 * Locação e Trade-in: "Em andamento" (ilustrativa; sem mudança nesta frente).
 */
export function situacaoPedido(p: Pick<ConteudoPedido, 'tipo' | 'itens'>, ordens: OrdemDoPedido[]): SituacaoPedido {
  if (p.tipo !== 'venda') return 'Em andamento';
  const total = p.itens.filter((i) => i.entidadeId).reduce((s, i) => s + i.quantidade, 0);
  const soma = (r: Record<string, number>) => Object.values(r).reduce((t, x) => t + x, 0);
  const faturado = ordens.reduce((s, o) => s + soma(o.faturadoPorItem), 0);
  if (total > 0 && faturado >= total) return 'Faturado';
  const aberta = ordens.some((o) => !o.cancelada && soma(o.faturadoPorItem) < o.itens.reduce((t, i) => t + i.quantidade, 0));
  const semOrdem = p.itens.some((i) => i.entidadeId && i.quantidade > ordens.reduce((s, o) => s + o.itens.filter((x) => x.itemId === i.itemId).reduce((t, x) => t + x.quantidade, 0), 0));
  if (faturado === 0) return ordens.length > 0 && ordens.every((o) => o.cancelada) ? (semOrdem ? 'Parcialmente cancelado' : 'Cancelado') : 'Em andamento';
  return !aberta && !semOrdem && ordens.some((o) => o.cancelada) ? 'Faturado parcialmente · saldo cancelado' : 'Faturado parcialmente';
}

/** P2-21 (D2): valor (R$) do saldo cancelado — quantidade NÃO faturada das Ordens canceladas × preço do item no Pedido.
 * P3-35: só o cancelamento efetivo (sem substituta, fora da retificação) — o saldo substituído não é subtraído duas vezes. */
export function saldoCanceladoValor(p: Pick<ConteudoPedido, 'itens'>, ordens: OrdemDoPedido[]): number {
  const preco = new Map(p.itens.map((i) => [i.itemId, i.precoUnit]));
  const v = ordens.filter((o) => o.cancelada && o.cancelamentoEfetivo !== false).reduce((s, o) => s + o.itens.reduce((t, i) => t + Math.max(0, i.quantidade - (o.faturadoPorItem[i.itemId] ?? 0)) * (preco.get(i.itemId) ?? 0), 0), 0);
  return Math.round(v * 100) / 100;
}

/** D3: margem (R$) do saldo cancelado — quantidade NÃO faturada das Ordens canceladas × (preço − custo) do item no Pedido. */
export function margemSaldoCanceladoValor(p: Pick<ConteudoPedido, 'itens'>, ordens: OrdemDoPedido[]): number {
  const m = new Map(p.itens.map((i) => [i.itemId, i.precoUnit - i.custoUnit]));
  const v = ordens.filter((o) => o.cancelada && o.cancelamentoEfetivo !== false).reduce((s, o) => s + o.itens.reduce((t, i) => t + Math.max(0, i.quantidade - (o.faturadoPorItem[i.itemId] ?? 0)) * (m.get(i.itemId) ?? 0), 0), 0);
  return Math.round(v * 100) / 100;
}

/** Saldo por entidade faturadora ainda sem Ordem (prévia da conferência antes de gerar as Ordens). */
export function previaOrdens(p: Pick<ConteudoPedido, 'itens'>, ordens: OrdemDoPedido[]) {
  const porEntidade = new Map<string, { itemId: string; quantidade: number }[]>();
  for (const i of p.itens) {
    if (!i.entidadeId) continue;
    const saldo = i.quantidade - quantidadeEmOrdens(ordens, i.itemId);
    if (saldo <= 0) continue;
    porEntidade.set(i.entidadeId, [...(porEntidade.get(i.entidadeId) ?? []), { itemId: i.itemId, quantidade: saldo }]);
  }
  return porEntidade;
}

export const NOME_DOCUMENTO = { locacao: 'Ordem de Locação ao Parceiro', trade_in: 'Ordem de Trade-in ao Comprador' } as const;
export const SITUACOES_DOCUMENTO = { em_conferencia: 'Em conferência', emitida: 'Emitida', cancelada: 'Cancelada' } as const;
export type SituacaoDocumento = keyof typeof SITUACOES_DOCUMENTO;

/**
 * Referência da PTAX de cada item em moeda estrangeira, só para EXIBIÇÃO na OCF (decisão de 08/10/2026): o valor
 * usado no Pedido e a data de referência registrada na cotação da versão aceita (manual, se houver; senão a oficial).
 * Não recalcula nada: se a cotação não tiver data ou o valor não for o mesmo do Pedido, não há data a mostrar.
 */
export function referenciasPtax(
  versao: { itens?: { id: string; cotacaoId?: string }[]; cotacoes?: { id: string; ptax?: { manual?: { valor: number; dataReferencia: string }; oficial?: { valor: number; dataReferencia: string } } }[]; ptaxProposta?: { manual?: { valor: number; dataReferencia: string }; oficial?: { valor: number; dataReferencia: string } } } | null | undefined,
  itensPedido: { itemId: string; ptax?: number }[],
): Record<string, { valor: number; data: string | null }> {
  const r: Record<string, { valor: number; data: string | null }> = {};
  for (const ip of itensPedido) {
    if (typeof ip.ptax !== 'number') continue;
    const iv = versao?.itens?.find((x) => x.id === ip.itemId);
    const cot = iv?.cotacaoId ? versao?.cotacoes?.find((c) => c.id === iv.cotacaoId) : undefined;
    // P5: versão de origem com a PTAX da proposta → a referência é a da proposta (uma só para todos os itens);
    // versão anterior → a da cotação selecionada, como foi emitida
    const ref = versao?.ptaxProposta ? (versao.ptaxProposta.manual ?? versao.ptaxProposta.oficial) : (cot?.ptax?.manual ?? cot?.ptax?.oficial);
    r[ip.itemId] = { valor: ip.ptax, data: ref && Math.abs(ref.valor - ip.ptax) < 1e-9 && ref.dataReferencia ? ref.dataReferencia : null };
  }
  return r;
}

/** Cancelamento de Ordem: o motivo "Outro" exige a Observação (tela e servidor; decisão de 09/10/2026). */
export const motivoExigeObservacao = (motivo: string) => motivo.trim().toLocaleLowerCase('pt-BR') === 'outro';
